import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { frappePost } from '@/lib/frappe';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';

// Display-only currency conversion: all charges stay in USD; this converts
// what the visitor SEES. Rates come from the backend proxy (cached 12h).

export const SUPPORTED_CURRENCIES = [
  'USD', 'AED', 'SAR', 'QAR', 'KWD', 'BHD', 'OMR', 'JOD', 'EGP', 'MAD',
  'TRY', 'PKR', 'INR', 'BDT', 'IDR', 'MYR', 'NGN', 'GBP', 'EUR', 'CAD', 'AUD',
] as const;

export type Currency = (typeof SUPPORTED_CURRENCIES)[number];

const STORAGE_KEY = 'hudan-currency';

interface RatesResponse {
  base: string;
  rates: Record<string, number>;
  fetched_at: string | null;
}

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (c: Currency) => void;
  /** Format a USD amount in the selected display currency. */
  formatPrice: (usdAmount: number) => string;
  /** True when a non-USD currency is selected and a rate is available. */
  isConverted: boolean;
}

const CurrencyContext = createContext<CurrencyContextType>({
  currency: 'USD',
  setCurrency: () => undefined,
  formatPrice: (usd) => `$${usd}`,
  isConverted: false,
});

function getInitialCurrency(): Currency {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && (SUPPORTED_CURRENCIES as readonly string[]).includes(stored)) {
    return stored as Currency;
  }
  return 'USD';
}

export const CurrencyProvider = ({ children }: { children: React.ReactNode }) => {
  const { language } = useLanguage();
  const { profile, isLoggedIn } = useAuth();
  const [currency, setCurrencyState] = useState<Currency>(getInitialCurrency);

  // A signed-in user's saved profile currency wins over the local default
  const profileCurrency = (profile as { preferred_currency?: string } | null)?.preferred_currency;
  useEffect(() => {
    if (
      profileCurrency &&
      (SUPPORTED_CURRENCIES as readonly string[]).includes(profileCurrency)
    ) {
      setCurrencyState(profileCurrency as Currency);
    }
  }, [profileCurrency]);

  const { data: ratesData } = useQuery({
    queryKey: ['exchange-rates'],
    queryFn: () => frappePost<RatesResponse>('hudan_lms.api.currency.get_exchange_rates'),
    staleTime: 12 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    // Prices render fine in USD while this loads or if it fails
    retry: 1,
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, currency);
  }, [currency]);

  const setCurrency = useCallback(
    (c: Currency) => {
      setCurrencyState(c);
      // Persist on the signed-in user's profile so it follows them across
      // devices; fire-and-forget — localStorage already covers this device.
      if (isLoggedIn) {
        frappePost('hudan_lms.api.currency.set_preferred_currency', { currency: c }).catch(
          () => undefined
        );
      }
    },
    [isLoggedIn]
  );

  const rate = currency === 'USD' ? 1 : ratesData?.rates?.[currency];
  const isConverted = currency !== 'USD' && typeof rate === 'number' && rate > 0;

  const formatPrice = useCallback(
    (usdAmount: number) => {
      const amount = Number(usdAmount) || 0;
      if (!isConverted) {
        return `$${amount}`;
      }
      const locale = language === 'ar' ? 'ar' : 'en';
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          maximumFractionDigits: 0,
        }).format(amount * (rate as number));
      } catch {
        return `${Math.round(amount * (rate as number))} ${currency}`;
      }
    },
    [isConverted, rate, currency, language]
  );

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, formatPrice, isConverted }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);
